import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

// Auth
import { AuthProvider, ProtectedRoute, useAuth } from "@/contexts/AuthContext";

// Components
import { LoginPage } from "@/components/LoginPage";
import { Sidebar } from "@/components/Sidebar";
import { NotificationBell } from "@/components/NotificationBell";
import { StatsDashboard } from "@/components/dashboards/DashboardViews";
import { MyStatusPanel } from "@/components/administration/MyStatusPanel";
import { MyBonusesPanel } from "@/components/administration/MyBonusesPanel";
import { CelebrationsCard, AnnouncementsCard, AttendanceRankingCard, AnnouncementsManager } from "@/components/intranet/IntranetWidgets";
import TeamDashboard from "@/components/intranet/TeamDashboard";
import { TicketsList, NewTicketForm, TicketDetail } from "@/components/tickets/TicketsModule";
import { TicketsAdmin } from "@/components/tickets/TicketsAdmin";
import TicketMonitor from "@/components/tickets/TicketMonitor";
import { UsersManagement, SoftwareTypesManagement, HardwareTypesManagement, StatusTypesManagement, PolicyTypesManagement } from "@/components/admin/AdminModule";
import { ComercialModule } from "@/components/comercial/ComercialModule";
import { ComercialAdmin } from "@/components/comercial/ComercialAdmin";
import { NegociacionesBoard } from "@/components/comercial/NegociacionesBoard";
import { POSSalesScreen } from "@/components/pos/POSSalesScreen";
import { POSDashboard } from "@/components/pos/POSDashboard";
import { POSCashRegister } from "@/components/pos/POSCashRegister";
import { POSInventory } from "@/components/pos/POSInventory";
import { POSSalesHistory } from "@/components/pos/POSSalesHistory";
import { POSReports } from "@/components/pos/POSReports";
import { POSReturns } from "@/components/pos/POSReturns";
import { POSConfig } from "@/components/pos/POSConfig";
import { PurchasesModule } from "@/components/pos/PurchasesModule";
import { POSCredits } from "@/components/pos/POSCredits";
import { MonthlyReports } from "@/components/reports/MonthlyReports";
import QuotationModule from "@/components/quotations/QuotationModule";
import QuotationPublicView from "@/components/quotations/QuotationPublicView";
import POSPublicView from "@/components/pos/POSPublicView";
import PaymentReceiptView from "@/components/pos/PaymentReceiptView";
import CxCPortalView from "@/components/pos/CxCPortalView";
import AdministrationModule from "@/components/administration/AdministrationModule";
import { HRModule } from "@/components/administration/HRModule";
import { InternalClientsModule } from "@/components/administration/InternalClientsModule";
import { CreditClientsModule } from "@/components/administration/CreditClientsModule";
import ViaticosModule from "@/components/viaticos/ViaticosModule";
import { AuditLogModule } from "@/components/admin/AuditLogModule";
import MarketingModule from "@/components/marketing/MarketingModule";
import { MarketingDashboard } from "@/components/marketing/MarketingDashboard";
import KPIModule from "@/components/kpi/KPIModule";
import ProductsModule from "@/components/products/ProductsModule";
import ProductPublicView from "@/components/products/ProductPublicView";
import TicketPublicView from "@/components/tickets/TicketPublicView";
import { ChatPanel } from "@/components/chat/ChatPanel";

import { API } from "@/lib/api";
import ClientPublicRegister from "@/components/administration/ClientPublicRegister";

const PublicQuotationPage = () => {
  const { publicToken } = useParams();
  return <QuotationPublicView publicToken={publicToken} />;
};

const PublicSalePage = () => {
  const { publicToken } = useParams();
  return <POSPublicView publicToken={publicToken} />;
};

const PublicClientRegisterPage = () => {
  const { registrationToken } = useParams();
  return <ClientPublicRegister registrationToken={registrationToken} />;
};

const PublicReceiptPage = () => {
  const { publicToken } = useParams();
  return <PaymentReceiptView publicToken={publicToken} />;
};

const PublicPortalPage = () => {
  const { token } = useParams();
  return <CxCPortalView token={token} />;
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [quotationPrefill, setQuotationPrefill] = useState(null);
  const [quotationViewId, setQuotationViewId] = useState(null);
  const { user, token } = useAuth();

  useEffect(() => {
    if (!token || !user) return;
    const checkReminders = async () => {
      try {
        const res = await axios.get(`${API}/reminders/pending`, { headers: { Authorization: `Bearer ${token}` } });
        let total = res.data.length;
        if (['admin', 'comercial', 'subgerente'].includes(user.role)) {
          const comRes = await axios.get(`${API}/comercial-reminders/pending`, { headers: { Authorization: `Bearer ${token}` } });
          total += comRes.data.length;
        }
        if (total > 0) {
          toast.warning(`Tienes ${total} recordatorio(s) pendiente(s)`, {
            duration: 8000,
            description: "Revisa tu dashboard para ver los detalles",
          });
        }
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
    };
    const timer = setTimeout(checkReminders, 1500);
    return () => clearTimeout(timer);
  }, [token, user]);

  const [ticketsInitialTab, setTicketsInitialTab] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleSelectTicket = (ticketId) => { setSelectedTicketId(ticketId); setActiveTab("ticket-detail"); };
  const handleBackFromTicket = () => { setSelectedTicketId(null); setTicketsInitialTab("lista"); setActiveTab("tickets"); };
  const handleTicketCreated = () => { setTicketsInitialTab("lista"); setActiveTab("tickets"); };
  const handleNewTicket = () => { setActiveTab("new-ticket"); };
  const handleCreateQuotationFromContact = (contactData) => { setQuotationPrefill(contactData); setQuotationViewId(null); setActiveTab("quotations"); };
  const handleViewQuotation = (quotationId) => { setQuotationViewId(quotationId); setQuotationPrefill(null); setActiveTab("quotations"); };

  const handleNotificationNavigate = (link) => {
    if (link && link.startsWith("/tickets/")) {
      setSelectedTicketId(link.replace("/tickets/", ""));
      setActiveTab("ticket-detail");
    } else if (link === "/tickets") {
      setActiveTab("tickets");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        if (user?.role === "marketing") {
          return <TeamDashboard />;
        }
        return <TeamDashboard />;
      case "tickets":
        return <TicketsAdmin onSelectTicket={handleSelectTicket} onNewTicket={handleNewTicket} user={user} initialTab={ticketsInitialTab} onTabReset={() => setTicketsInitialTab(null)} />;
      case "ticket-monitor":
        return <TicketMonitor />;
      case "new-ticket":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Crear Ticket</h1><NewTicketForm onSuccess={handleTicketCreated} /></>);
      case "ticket-detail":
        return <TicketDetail ticketId={selectedTicketId} onBack={handleBackFromTicket} />;
      case "comercial":
        if (['admin', 'comercial', 'ingeniero', 'auxiliar', 'subgerente'].includes(user?.role)) {
          return <ComercialAdmin defaultTab="contacts" onCreateQuotation={handleCreateQuotationFromContact} onViewQuotation={handleViewQuotation} />;
        }
        return <ComercialModule onCreateQuotation={handleCreateQuotationFromContact} onViewQuotation={handleViewQuotation} />;
      case "negociaciones":
        return <NegociacionesBoard onNavigateToContact={(cid) => { setActiveTab("comercial"); }} />;
      case "pos-dashboard":
        return <POSDashboard />;
      case "pos-ventas":
        return <POSSalesScreen />;
      case "pos-compras":
        return <PurchasesModule />;
      case "pos-creditos":
        return <POSCredits />;
      case "pos-productos":
        return <ProductsModule />;
      case "pos-corte":
        return <POSCashRegister />;
      case "pos-historial":
        return <POSSalesHistory />;
      case "pos-reportes":
        return <POSReports />;
      case "pos-devoluciones":
        return <POSReturns onClose={() => setActiveTab("pos-historial")} />;
      case "pos-config":
        return <POSConfig />;
      case "users":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Usuarios</h1><UsersManagement /></>);
      case "software-types":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Software</h1><SoftwareTypesManagement /></>);
      case "hardware-types":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Hardware</h1><HardwareTypesManagement /></>);
      case "status-types":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Estatus</h1><StatusTypesManagement /></>);
      case "policy-types":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Polizas</h1><PolicyTypesManagement /></>);
      case "reports":
        if (user?.role !== 'admin') return null;
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Reportes</h1><MonthlyReports /></>);
      case "announcements":
        return (<><h1 className="text-2xl font-bold text-slate-900 mb-6">Avisos e Intranet</h1><AnnouncementsManager /></>);
      case "quotations":
        return <QuotationModule token={token} user={user} prefillData={quotationPrefill} onClearPrefill={() => setQuotationPrefill(null)} viewDetailId={quotationViewId} onClearViewDetail={() => setQuotationViewId(null)} />;
      case "admin-finanzas":
        return <AdministrationModule />;
      case "admin-rrhh":
        return <HRModule />;
      case "admin-clientes":
        return <CreditClientsModule />;
      case "viaticos":
        return <ViaticosModule />;
      case "audit-log":
        return <AuditLogModule />;
      case "marketing":
        return <MarketingModule />;
      case "kpi":
        return <KPIModule />;
      case "chat":
        return <ChatPanel />;
      case "productos":
        return <ProductsModule readOnly />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-layout" data-testid="dashboard">
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="p-1" data-testid="mobile-menu-btn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="17" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <span className="font-bold text-sm">MaxTicket</span>
        </div>
        <NotificationBell onNavigate={handleNotificationNavigate} />
      </div>
      {/* Sidebar overlay for mobile */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'sidebar-open' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar activeTab={activeTab} setActiveTab={(tab) => { setActiveTab(tab); setSidebarOpen(false); }} className={sidebarOpen ? 'sidebar-open' : ''} />
      <main className="main-content">
        <div className="hidden md:flex items-center justify-end mb-4 -mt-2" data-testid="top-bar">
          <NotificationBell onNavigate={handleNotificationNavigate} />
        </div>
        {renderContent()}
      </main>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/cotizacion/:publicToken" element={<PublicQuotationPage />} />
            <Route path="/venta/:publicToken" element={<PublicSalePage />} />
            <Route path="/producto/:linkId" element={<ProductPublicView />} />
            <Route path="/ticket/:linkId" element={<TicketPublicView />} />
            <Route path="/yotepuedoayudar/:registrationToken" element={<PublicClientRegisterPage />} />
            <Route path="/recibo/:publicToken" element={<PublicReceiptPage />} />
            <Route path="/estado-cuenta/:token" element={<PublicPortalPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
